import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { BillingFormRule, BillingFormSchema } from "@factory/form/form.rule";
import { useFormDefaultBilling } from "@factory/form/form-default-billing.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateBilling,
  useQyGetBillingById,
  useQyDeleteBilling,
} from "@factory/query/billing.query";
import { GetResponseBillingModel } from "@factory/model/query-model-billing";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditBilling() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultBilling();
  const navigate = useNavigate();
  const { mapUpdateBilling, mapDeleteBilling } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { billingId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Billing
  const handleGetApiSuccess = (data: GetResponseBillingModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("contract", responseData?.contract || "");
    setValue("session", responseData?.session);
  };
  const {
    data: billingResponse,
    isLoading,
    isError: billingError,
  } = useQyGetBillingById(billingId || "", handleGetApiSuccess);
  const billingData = billingResponse?.data?.[0];

  // API UPDATE Billing
  const handleUpdateApiSuccess = () => {
    showSuccess("Billing updated successfully");
  };
  const { mutate: updateBilling, isLoading: isEditLoading } =
    useQyUpdateBilling(handleUpdateApiSuccess);

  // API DELETE Billing
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Billing deleted successfully");
  };
  const { mutate: deleteBilling } = useQyDeleteBilling(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<BillingFormSchema>({
    defaultValues: getDefault(billingData),
    resolver: zodResolver(BillingFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: BillingFormSchema) => {
    if (!billingData || !billingData.code) {
      throw new Error("No billing data");
    }

    const formData = mapUpdateBilling(form, billingData.code);
    updateBilling(formData);
  };
  const handleValidateError = (err: FieldErrors<BillingFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!billingData || !billingData.code) {
      throw new Error("No billing data found");
    }

    const form = mapDeleteBilling(billingData.code);
    deleteBilling(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    billingId,
    billingData,
    isLoading,
    billingError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
